import { useEffect, useState } from 'react';
import type { AuthInfo } from '../../model/authInfo';

const isExpired = (grant: AuthInfo.ActiveAccountGrant | undefined): boolean => !!grant && grant.expiresAt.valueOf() <= Date.now();

export const getNextActiveGrantExpiryAt = (authInfo: AuthInfo | undefined): number | undefined => {
  return [authInfo?.account.activeSystemAdminGrant, authInfo?.account.activePiiAccessGrant].reduce<number | undefined>(
    (nextExpiryAt, grant) => {
      if (!grant) return nextExpiryAt;
      const expiresAt = grant.expiresAt.valueOf();
      return nextExpiryAt === undefined || expiresAt < nextExpiryAt ? expiresAt : nextExpiryAt;
    },
    undefined,
  );
};

export const maskExpiredActiveGrants = (authInfo: AuthInfo | undefined): AuthInfo | undefined => {
  if (!authInfo) return undefined;

  const systemAdminExpired = isExpired(authInfo.account.activeSystemAdminGrant);
  const piiAccessExpired = isExpired(authInfo.account.activePiiAccessGrant);
  if (!systemAdminExpired && !piiAccessExpired) return authInfo;

  let account = authInfo.account;

  if (systemAdminExpired) {
    const { activeSystemAdminGrant: _expiredActiveSystemAdminGrant, ...rest } = account;
    account = {
      ...rest,
      hasActiveSystemAdmin: false,
      isSystemAdmin: false,
    };
  }

  if (piiAccessExpired) {
    const { activePiiAccessGrant: _expiredActivePiiAccessGrant, ...rest } = account;
    account = {
      ...rest,
      hasActivePiiAccess: false,
    };
  }

  return { ...authInfo, account };
};

export const useActiveGrantExpiryRefresh = (params: { authInfo: AuthInfo | undefined; refetch: () => unknown }): AuthInfo | undefined => {
  const [, setExpiryRevision] = useState(0);
  const nextGrantExpiryAt = getNextActiveGrantExpiryAt(params.authInfo);

  useEffect(() => {
    if (nextGrantExpiryAt === undefined) return;

    const delay = Math.max(nextGrantExpiryAt - Date.now() + 1000, 0);
    const timer = setTimeout(() => {
      setExpiryRevision((current) => current + 1);
      void params.refetch();
    }, delay);

    return () => clearTimeout(timer);
  }, [nextGrantExpiryAt, params.refetch]);

  return maskExpiredActiveGrants(params.authInfo);
};
